<template>
  <v-container class="py-6" style="max-width: 900px;">
    <h2 class="mb-1">Ustawienia konta</h2>
    <p class="grey--text mb-6">Zarządzaj swoim profilem, zdjęciem, faksymilką i hasłem.</p>

    <!-- Profil + zdjęcie -->
    <v-card class="mb-6" outlined>
      <v-card-title>Profil</v-card-title>
      <v-card-text>
        <div class="d-flex align-center flex-wrap">
          <v-avatar size="96" color="grey lighten-2" class="mr-6 mb-3">
            <img v-if="avatar" :src="avatar" alt="avatar" />
            <v-icon v-else size="56" color="grey darken-1">mdi-account</v-icon>
          </v-avatar>
          <div class="mb-3">
            <div class="text-h6">{{ (profile.firstName + ' ' + profile.lastName).trim() || '—' }}</div>
            <div class="grey--text">{{ profile.email }}</div>
            <div class="grey--text">{{ roleLabel(profile.role) }}<span v-if="profile.company"> · {{ profile.company.name }}</span></div>
            <div class="mt-3">
              <v-btn small color="primary" outlined class="mr-2 text-none" @click="$refs.avatarInput.click()">
                <v-icon left small>mdi-camera</v-icon> Zmień zdjęcie
              </v-btn>
              <v-btn v-if="avatar" small text color="error" class="text-none" :loading="avatarSaving" @click="removeAvatar">
                Usuń zdjęcie
              </v-btn>
              <input ref="avatarInput" type="file" accept="image/png,image/jpeg,image/webp" class="d-none" @change="onAvatarFile" />
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Nazwa w czacie -->
    <v-card class="mb-6" outlined>
      <v-card-title>Nazwa w czacie</v-card-title>
      <v-card-text>
        <v-text-field
          v-model="chatAlias"
          label="Wyświetlana nazwa"
          placeholder="np. KJ"
          maxlength="32"
          counter="32"
          hide-details="auto"
          outlined
          dense
          style="max-width: 320px"
        />
        <div class="caption grey--text mt-2">
          Tak podpisane będą Twoje wiadomości, wskaźnik „pisze…" i potwierdzenia
          odczytu. Puste pole = imię i nazwisko.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" class="text-none" :loading="chatAliasSaving" @click="saveChatAlias">Zapisz</v-btn>
        <v-btn v-if="chatAlias" text class="text-none" :loading="chatAliasSaving" @click="chatAlias = ''; saveChatAlias()">
          Przywróć imię i nazwisko
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Urlopy -->
    <leave-section class="mb-6" />

    <!-- Dźwięk powiadomień (ustawienie per przeglądarka) -->
    <v-card class="mb-6" outlined>
      <v-card-title>Dźwięk powiadomień</v-card-title>
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" md="4">
            <v-select
              v-model="soundSettings.sound"
              :items="soundOptions"
              label="Dźwięk"
              dense outlined hide-details
              @change="onSoundChange"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-slider
              v-model="soundSettings.volume"
              :disabled="soundSettings.sound === 'brak'"
              label="Głośność"
              min="0" max="100" step="5"
              thumb-label hide-details
              prepend-icon="mdi-volume-low"
              append-icon="mdi-volume-high"
              @change="onSoundChange"
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn small outlined color="primary" class="text-none" :disabled="soundSettings.sound === 'brak'" @click="playSample">
              <v-icon left small>mdi-play</v-icon> Próbka
            </v-btn>
          </v-col>
        </v-row>
        <div class="caption grey--text mt-2">
          Dotyczy powiadomień czatu i systemowych w tej przeglądarce. Zapisuje się automatycznie.
        </div>
      </v-card-text>
    </v-card>

    <!-- Faksymilka -->
    <v-card v-if="canUseFacsimile" class="mb-6" outlined>
      <v-card-title>Faksymilka (podpis do pism)</v-card-title>
      <v-card-text>
        <div v-if="facsimile" class="mb-3">
          <div class="caption grey--text mb-1">Aktualna faksymilka:</div>
          <img :src="facsimile" style="max-height:90px; border:1px solid #ddd; background:#fff; border-radius:6px;" />
        </div>
        <div class="caption mb-1">Narysuj nowy podpis:</div>
        <SignatureField
          width="100%" height="180px" ref="facsimilePad"
          class="signature-pad" style="border:1px solid #ccc; border-radius:6px; background:#fff;"
        />
        <v-btn x-small text class="mt-1" @click="clearFacsimilePad">Wyczyść</v-btn>
        <div class="caption grey--text mt-2">lub wgraj obraz podpisu (PNG/JPG):</div>
        <v-file-input dense outlined accept="image/png,image/jpeg" label="Plik podpisu" prepend-icon="mdi-image" @change="onFacsimileFile" />
      </v-card-text>
      <v-card-actions>
        <v-btn v-if="facsimile" text color="error" @click="removeFacsimile">Usuń faksymilkę</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" :loading="facsimileSaving" @click="saveFacsimile">Zapisz faksymilkę</v-btn>
      </v-card-actions>
    </v-card>
    <v-alert v-else type="info" text dense class="mb-6">
      Nie masz uprawnienia do faksymilki. Poproś administratora o jego włączenie.
    </v-alert>

    <!-- Zmiana hasła -->
    <v-card class="mb-6" outlined>
      <v-card-title>Zmiana hasła</v-card-title>
      <v-card-text>
        <v-form ref="pwdForm" v-model="pwdValid">
          <v-text-field
            v-model="pwd.current" label="Obecne hasło" type="password" outlined dense
            :rules="[v => !!v || 'Podaj obecne hasło']" autocomplete="current-password"
          />
          <v-text-field
            v-model="pwd.next" label="Nowe hasło" type="password" outlined dense
            :rules="passwordRules" autocomplete="new-password"
            hint="Min. 8 znaków, wielka i mała litera, cyfra i znak specjalny (np. !@#$)." persistent-hint
          />
          <v-text-field
            class="mt-3"
            v-model="pwd.confirm" label="Powtórz nowe hasło" type="password" outlined dense
            :rules="[v => v === pwd.next || 'Hasła nie są takie same']" autocomplete="new-password"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" :loading="pwdSaving" @click="changePassword">Zmień hasło</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import LeaveSection from "@/components/LeaveSection.vue";
import SignatureField from "@/components/SignatureField.vue";
import axios from "axios";
import Swal from "sweetalert2";
import { SOUND_OPTIONS, getSoundSettings, saveSoundSettings, playNotifSound } from "@/services/notifSound";

export default {
  components: { LeaveSection, SignatureField },
  name: "AccountSettingsPage",
  data: () => ({
    soundOptions: SOUND_OPTIONS,
    soundSettings: getSoundSettings(),
    profile: { firstName: "", lastName: "", email: "", role: "", company: null },
    avatar: null,
    avatarSaving: false,
    canUseFacsimile: false,
    chatAlias: '',
    chatAliasSaving: false,
    facsimile: null,
    facsimileUpload: null,
    facsimileSaving: false,
    pwd: { current: "", next: "", confirm: "" },
    pwdValid: false,
    pwdSaving: false,
    passwordRules: [
      v => !!v || "Podaj nowe hasło",
      v => (v && v.length >= 8) || "Min. 8 znaków",
      v => /[A-Z]/.test(v || "") || "Wymagana wielka litera",
      v => /[a-z]/.test(v || "") || "Wymagana mała litera",
      v => /[0-9]/.test(v || "") || "Wymagana cyfra",
      v => /[^A-Za-z0-9]/.test(v || "") || "Wymagany znak specjalny",
    ],
  }),
  mounted() {
    this.loadProfile();
  },
  methods: {
    onSoundChange() {
      saveSoundSettings(this.soundSettings);
      playNotifSound(this.soundSettings); // od razu słychać efekt zmiany
    },
    playSample() {
      playNotifSound(this.soundSettings);
    },
    headers() {
      return { Authorization: "Bearer " + localStorage.getItem("token") };
    },
    roleLabel(role) {
      const map = {
        headadmin: "Head admin", superadmin: "Administrator", user: "Manager",
        office: "Biuro", admin: "Administrator",
      };
      return map[role] || role || "";
    },
    async loadProfile() {
      try {
        const me = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "company-admins/me",
          headers: this.headers(),
        });
        const d = me.data.data || {};
        this.profile = {
          firstName: d.firstName || "", lastName: d.lastName || "",
          email: d.email || "", role: d.role || "", company: d.company || null,
        };
        this.avatar = d.avatar || null;
        this.canUseFacsimile = !!d.canUseFacsimile;
        this.chatAlias = d.chatAlias || '';
        this.facsimile = d.facsimileSignature || null;
      } catch (e) {
        Swal.fire("Błąd", "Nie udało się wczytać profilu", "error");
      }
    },
    // ----- Zdjęcie -----
    onAvatarFile(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.size > 3 * 1024 * 1024) {
        Swal.fire("Za duży plik", "Maksymalny rozmiar zdjęcia to 3 MB.", "warning");
        e.target.value = "";
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => this.saveAvatar(ev.target.result);
      reader.readAsDataURL(file);
      e.target.value = "";
    },
    async saveAvatar(dataUrl) {
      this.avatarSaving = true;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "company-admins/me/avatar",
          headers: this.headers(),
          data: { avatar: dataUrl },
        });
        this.avatar = dataUrl;
        this.$root.$emit("profile-updated");
        Swal.fire("Sukces", "Zdjęcie zaktualizowane", "success");
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.avatarSaving = false;
    },
    async saveChatAlias() {
      this.chatAliasSaving = true;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "company-admins/me/chat-alias",
          headers: this.headers(),
          data: { chatAlias: this.chatAlias },
        });
        Swal.fire(
          "Zapisano",
          this.chatAlias
            ? `W czacie będziesz widoczny jako „${this.chatAlias}".`
            : "Wróciliśmy do imienia i nazwiska.",
          "success",
        );
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.chatAliasSaving = false;
    },

    async removeAvatar() {
      this.avatarSaving = true;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "company-admins/me/avatar",
          headers: this.headers(),
          data: { avatar: null },
        });
        this.avatar = null;
        this.$root.$emit("profile-updated");
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.avatarSaving = false;
    },
    // ----- Faksymilka -----
    clearFacsimilePad() {
      if (this.$refs.facsimilePad) this.$refs.facsimilePad.clearSignature();
    },
    onFacsimileFile(file) {
      if (!file) { this.facsimileUpload = null; return; }
      const reader = new FileReader();
      reader.onload = (e) => { this.facsimileUpload = e.target.result; };
      reader.readAsDataURL(file);
    },
    async saveFacsimile() {
      let signature = this.facsimileUpload;
      if (!signature && this.$refs.facsimilePad) {
        const { isEmpty, data } = this.$refs.facsimilePad.saveSignature();
        if (!isEmpty) signature = data;
      }
      if (!signature) {
        Swal.fire("Brak podpisu", "Narysuj podpis lub wgraj obraz", "warning");
        return;
      }
      this.facsimileSaving = true;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "company-admins/me/facsimile",
          headers: this.headers(),
          data: { facsimileSignature: signature },
        });
        this.facsimile = signature;
        this.facsimileUpload = null;
        this.clearFacsimilePad();
        Swal.fire("Sukces", "Zapisano faksymilkę", "success");
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.facsimileSaving = false;
    },
    async removeFacsimile() {
      this.facsimileSaving = true;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "company-admins/me/facsimile",
          headers: this.headers(),
          data: { facsimileSignature: null },
        });
        this.facsimile = null;
        Swal.fire("Sukces", "Usunięto faksymilkę", "success");
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.facsimileSaving = false;
    },
    // ----- Hasło -----
    async changePassword() {
      if (!this.$refs.pwdForm.validate()) return;
      this.pwdSaving = true;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "company-admins/me/password",
          headers: this.headers(),
          data: { currentPassword: this.pwd.current, newPassword: this.pwd.next },
        });
        this.pwd = { current: "", next: "", confirm: "" };
        this.$refs.pwdForm.resetValidation();
        Swal.fire("Sukces", "Hasło zostało zmienione", "success");
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.pwdSaving = false;
    },
  },
};
</script>
